import React, { useState } from "react";
import { X, MessageSquare, Plus } from "lucide-react";

export default function POLineNotesModal({
  isOpen,
  onClose,
  po,
  item,
  currentUser,
  onSave
}) {
  const [newNoteText, setNewNoteText] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen || !item) return null;

  // Aggregate all notes and logs across the line item and vendors for complete lifecycle traceability
  const allLogs = [];

  if (item.logs && Array.isArray(item.logs)) {
    item.logs.forEach((log) => {
      allLogs.push({
        ...log,
        source: log.source || "Line Item"
      });
    });
  }

  if (item.vendors && Array.isArray(item.vendors)) {
    item.vendors.forEach((v) => {
      if (v.logs && Array.isArray(v.logs)) {
        v.logs.forEach((log) => {
          allLogs.push({
            ...log,
            source: v.vendor ? `Vendor: ${v.vendor}` : "Vendor Split"
          });
        });
      }
    });
  }

  // Include legacy string notes if present and not already represented
  if (item.notes && !allLogs.some((l) => l.text === item.notes)) {
    allLogs.push({
      text: item.notes,
      type: "Legacy Note",
      timestamp: po?.date || po?.createdAt || new Date().toISOString(),
      source: "Estimate Note",
      author: "System"
    });
  }

  // Sort logs by date descending (newest on top)
  allLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const handleSave = async () => {
    if (!newNoteText.trim() || isSaving) return;
    try {
      setIsSaving(true);
      await onSave(po.id, item.id, newNoteText.trim());
      setNewNoteText("");
    } catch (err) {
      console.error("Failed to save line item note:", err);
      alert("Error saving note: " + (err.message || "Unknown error"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content" 
        style={{ maxWidth: "560px", width: "95%" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <MessageSquare size={18} style={{ color: "var(--primary)" }} />
            <h3 style={{ margin: 0 }}>Line Item Notes</h3>
            {po?.poNumber && (
              <span className="sku-badge" style={{ fontSize: "0.75rem", margin: 0 }}>
                {po.poNumber}
              </span>
            )}
          </div>
          <button className="modal-close-btn" onClick={onClose} title="Close">
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: "1.5rem" }}>
          {/* Item Metadata Banner */}
          <div style={{ 
            backgroundColor: "rgba(255, 255, 255, 0.03)", 
            border: "1px solid var(--border-color)", 
            borderRadius: "6px", 
            padding: "0.75rem 1rem", 
            marginBottom: "1.25rem" 
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.25rem" }}>
              <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)", fontWeight: 600 }}>
                Line #{item.lineNumber || "—"}
              </span>
              <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                Qty: <strong style={{ color: "var(--text-primary)" }}>{item.quantity ?? "—"}</strong>
              </span>
            </div>
            <div style={{ color: "var(--text-primary)", fontWeight: 600, fontSize: "0.95rem" }}>
              {item.description || item.name || "Unnamed Line Item"}
            </div>
          </div>

          {/* Unified Log History Stream */}
          <div style={{ marginBottom: "1.5rem" }}>
            <span className="procurement-modal-label" style={{ marginBottom: "0.5rem", fontWeight: 600 }}>
              Lifecycle Notes & Logs ({allLogs.length})
            </span>
            <div className="procurement-log-scroll" style={{ maxHeight: "260px" }}>
              {allLogs.map((log, idx) => (
                <div key={idx} className="procurement-log-entry">
                  <div className="procurement-log-header">
                    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
                      <span className="procurement-log-type">{log.type || "Note"}</span>
                      {log.source && (
                        <span style={{ 
                          fontSize: "0.7rem", 
                          color: "var(--text-secondary)", 
                          backgroundColor: "rgba(255, 255, 255, 0.05)", 
                          padding: "0.1rem 0.35rem", 
                          borderRadius: "3px" 
                        }}>
                          {log.source}
                        </span>
                      )}
                    </div>
                    <span className="procurement-log-timestamp">
                      {log.timestamp ? new Date(log.timestamp).toLocaleString() : "N/A"}
                      {log.author ? ` • ${log.author}` : ""}
                    </span>
                  </div>
                  <p className="procurement-log-text">{log.text}</p>
                </div>
              ))}
              {allLogs.length === 0 && (
                <div className="procurement-empty-text">
                  No notes recorded yet. Append a note below to track specifications, vendor remarks, or pricing context.
                </div>
              )}
            </div>
          </div>

          {/* Append New Note Input */}
          <div className="form-group" style={{ marginBottom: "1.5rem" }}>
            <label className="form-label" htmlFor="poLineItemNoteInput" style={{ fontWeight: 600 }}>
              Append New Note
            </label>
            <textarea
              id="poLineItemNoteInput"
              className="form-input"
              style={{ minHeight: "85px", padding: "0.75rem", resize: "vertical" }}
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              placeholder="Add vendor communications, customer requests, internal pricing remarks, or item specifications..."
              disabled={isSaving}
            />
          </div>

          {/* Modal Actions */}
          <div className="procurement-modal-actions">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onClose} 
              disabled={isSaving}
            >
              Close
            </button>
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={handleSave} 
              disabled={!newNoteText.trim() || isSaving}
              style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
            >
              {isSaving ? "Saving..." : "Save Note"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
